import { useEffect, useState } from "react";
import {
  Card, CardHeader, CardBody, CardFooter,
  Container, Row, Col, Input, Label, Button,
  Table, Pagination, PaginationItem, PaginationLink
} from "reactstrap";
import axios from "axios";
import Header from "components/Headers/Header";
import Loader from "components/Loader";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;

const ArbitragemComparativo = () => {
  const [utmType, setUtmType] = useState("utm_campaign");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [sortColumn, setSortColumn] = useState("revenue");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const y = dateStr.substring(0, 4);
    const m = dateStr.substring(4, 6);
    const d = dateStr.substring(6, 8);
    return `${d}/${m}/${y}`;
  };

  // 🚀 Buscar dados combinados
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // GA4
      const analyticsRes = await axios.post(`${API_URL}/analytics`, {
        utm:
          utmType === "utm_campaign"
            ? "sessionCampaignName"
            : utmType === "utm_medium"
              ? "sessionMedium"
              : "sessionSource",
        startDate: startDate || "yesterday",
        endDate: endDate || "yesterday",
      }, { headers });

      // Ad Manager
      const adManagerRes = await axios.post(`${API_URL}/admanager/keyvalue`, {
        filterType: utmType,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }, { headers });

      const analyticsData = analyticsRes.data || [];
      const adManagerData = adManagerRes.data.rows || [];

      // Combina UTM
      const combined = {};
      analyticsData.forEach((a) => {
        const key = a.utmValue || "(vazio)";
        if (!combined[key]) combined[key] = { utm: key, sessions: 0, users: 0 };
        combined[key].sessions += parseInt(a.sessions || 0);
        combined[key].users += parseInt(a.activeUsers || 0);
      });

      adManagerData.forEach((ad) => {
        const key = ad.value || "(vazio)";
        const date = ad.date || new Date().toISOString().split("T")[0].replace(/-/g, "");
        if (!combined[key]) combined[key] = { utm: key, sessions: 0, users: 0 };
        combined[key].date = formatDate(date);
        combined[key].revenue = (combined[key].revenue || 0) + Number(ad.revenue || 0);
        combined[key].ecpm = Number(ad.eCPM || 0);
        combined[key].clicks = (combined[key].clicks || 0) + Number(ad.clicks || 0);
        combined[key].impressions = (combined[key].impressions || 0) + Number(ad.impressions || 0);
      });

      const mergedData = Object.values(combined).map((item) => ({
        ...item,
        date: item.date || formatDate(new Date().toISOString().split("T")[0].replace(/-/g, "")),
        rpm: item.sessions > 0 ? (item.revenue / item.sessions) * 1000 : 0,
        ctr: item.impressions > 0 ? (item.clicks / item.impressions) * 100 : 0,
      }));

      setData(mergedData);
    } catch (err) {
      console.error("❌ Erro ao buscar dados:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [utmType]);

  // 🔄 Ordenação
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortColumn === "date") {
      const [da, ma, ya] = (a.date || "").split("/").map(Number);
      const [db, mb, yb] = (b.date || "").split("/").map(Number);
      const valA = new Date(ya, ma - 1, da).getTime();
      const valB = new Date(yb, mb - 1, db).getTime();
      return sortDirection === "asc" ? valA - valB : valB - valA;
    } else {
      const valA = Number(a[sortColumn]) || 0;
      const valB = Number(b[sortColumn]) || 0;
      return sortDirection === "asc" ? valA - valB : valB - valA;
    }
  });

  // 🔢 Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 📊 Dados do gráfico (5 maiores receitas)
  const chartData = [...data]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)
    .map((d) => ({
      ...d,
      sessions: Number(d.sessions?.toFixed(1) || 0),
      revenue: Number(d.revenue?.toFixed(1) || 0),
    }))
    .reverse();

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        {/* 🔍 Filtros */}
        <Row className="mb-4">
          <Col md="3">
            <Label>Início:</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Col>
          <Col md="3">
            <Label>Fim:</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
          <Col md="3">
            <Label>Dimensão UTM:</Label>
            <Input type="select" value={utmType} onChange={(e) => setUtmType(e.target.value)}>
              <option value="utm_campaign">utm_campaign</option>
              <option value="utm_medium">utm_medium</option>
              <option value="utm_source">utm_source</option>
            </Input>
          </Col>
          <Col md="3" className="d-flex align-items-end">
            <Button color="primary" onClick={fetchData}>🔄 Atualizar</Button>
          </Col>
        </Row>

        {/* 📊 Gráfico Comparativo */}
        <Row>
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">💸 Receita x Sessões por {utmType}</h3>
              </CardHeader>
              <CardBody
                className="d-flex justify-content-center align-items-center"
                style={{ height: 300 }}
              >
                {loading ? (
                  <Loader />
                ) : chartData.length === 0 ? (
                  <p className="text-center text-muted">Sem dados disponíveis.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="utm" />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#00C49F" />
                      <Tooltip formatter={(v) => v.toFixed(1)} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="sessions" fill="#8884d8" name="Sessões" />
                      <Bar yAxisId="right" dataKey="revenue" fill="#00C49F" name="Receita (USD)" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 📋 Tabela Detalhada */}
        <Row className="mt-4">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">📋 Detalhamento — {utmType}</h3>
              </CardHeader>
              {loading ? (
                <Loader />
              ) : (
                <>
                  <CardBody>
                    <Table responsive className="table-flush">
                      <thead className="thead-light">
                        <tr>
                          {[
                            { key: "date", label: "📅 Data" },
                            { key: "utm", label: "UTM" },
                            { key: "users", label: "Usuários" },
                            { key: "sessions", label: "Sessões" },
                            { key: "impressions", label: "Impressões" },
                            { key: "clicks", label: "Cliques" },
                            { key: "ctr", label: "CTR (%)" },
                            { key: "revenue", label: "Receita (USD)" },
                            { key: "ecpm", label: "eCPM" },
                            { key: "rpm", label: "RPM" },
                          ].map((col) => (
                            <th
                              key={col.key}
                              style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                              onClick={() => handleSort(col.key)}
                            >
                              {col.label}{" "}
                              {sortColumn === col.key
                                ? sortDirection === "asc"
                                  ? "▲"
                                  : "▼"
                                : ""}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.date}</td>
                            <td>{row.utm}</td>
                            <td>{row.users}</td>
                            <td>{row.sessions}</td>
                            <td>{row.impressions || 0}</td>
                            <td>{row.clicks || 0}</td>
                            <td>{row.ctr.toFixed(1)}%</td>
                            <td>${row.revenue?.toFixed(1) || "0.0"}</td>
                            <td>${row.ecpm?.toFixed(1) || "0.0"}</td>
                            <td>${row.rpm?.toFixed(1) || "0.0"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>

                  {/* 🔢 Paginação */}
                  <CardFooter className="py-4">
                    <Pagination className="pagination justify-content-end mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1) setCurrentPage(currentPage - 1);
                          }}
                        >
                          <i className="fas fa-angle-left" />
                        </PaginationLink>
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <PaginationItem key={i} active={currentPage === i + 1}>
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(i + 1);
                            }}
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                          }}
                        >
                          <i className="fas fa-angle-right" />
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </CardFooter>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ArbitragemComparativo;